import { localTodayIso } from '../../utils/bills';

/**
 * The periods Lançamentos offers, as date ranges on the user's own calendar.
 * Dates are built from local parts, never through toISOString(): in Brazil
 * that runs three hours ahead, so after 21h "today" used to become tomorrow.
 */
export type Period =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'allTime'
  | 'custom';

/** The ones shown as chips; the rest still read from a shared link. */
export const PERIOD_CHIPS: Period[] = ['thisMonth', 'lastMonth', 'last30days', 'thisYear', 'allTime', 'custom'];

const iso = (year: number, monthIndex: number, day: number) => localTodayIso(new Date(year, monthIndex, day));

export const periodRange = (period: Period, now: Date = new Date()): { startDate?: string; endDate?: string } => {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const today = iso(y, m, d);
  switch (period) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'yesterday':
      return { startDate: iso(y, m, d - 1), endDate: iso(y, m, d - 1) };
    case 'last7days':
      return { startDate: iso(y, m, d - 7), endDate: today };
    case 'last30days':
      return { startDate: iso(y, m, d - 30), endDate: today };
    case 'thisMonth':
      return { startDate: iso(y, m, 1), endDate: today };
    case 'lastMonth':
      return { startDate: iso(y, m - 1, 1), endDate: iso(y, m, 0) };
    case 'thisYear':
      return { startDate: iso(y, 0, 1), endDate: today };
    case 'lastYear':
      return { startDate: iso(y - 1, 0, 1), endDate: iso(y - 1, 11, 31) };
    default:
      return {};
  }
};

const NAMED: Period[] = ['today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear'];

/** Which period a pair of dates is; "custom" when it is none of them. */
export const detectPeriod = (startDate?: string, endDate?: string, now: Date = new Date()): Period => {
  if (!startDate && !endDate) return 'allTime';
  const match = NAMED.find((period) => {
    const range = periodRange(period, now);
    return range.startDate === startDate && range.endDate === endDate;
  });
  return match ?? 'custom';
};
